// Catalogue money input: shops type pesos with centavos ("₱12.50"), the wire carries integer
// centavos. Pure string maths (no floats, no Intl) so 0.1 + 0.2 never shows up in a price.

/** ₱1,000,000: a sanity ceiling for a single product price (the server only requires ≥ 0). */
export const MAX_PRICE_CENTAVOS = 100_000_000;
/** ₱100,000 per kg: the same ceiling as the Fresh setup validator (v1/fresh.js MAX_PPK). */
export const MAX_PPK_CENTAVOS = 10_000_000;

/**
 * Parse a peso amount typed by a person. Accepts "12", "12.5", "12.50", "₱1,212.50", " 0.75 ".
 * @param {unknown} input
 * @param {{ max?: number; label?: string }} [o]
 * @returns {{ ok: true; centavos: number } | { ok: false; empty: boolean; error: string }}
 */
export function parsePesos(input, o = {}) {
  const label = o.label || 'a price';
  const max = o.max ?? MAX_PRICE_CENTAVOS;
  const raw = String(input ?? '')
    .trim()
    .replace(/^₱\s*/, '')
    .replace(/,/g, '');
  if (raw === '') return { ok: false, empty: true, error: `Enter ${label}.` };
  if (raw.startsWith('-')) return { ok: false, empty: false, error: 'Prices can’t be negative.' };
  if (/^\d*\.\d{3,}$/.test(raw)) return { ok: false, empty: false, error: 'Use at most two digits for centavos.' };
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(raw);
  if (!m || (m[1] === '' && !m[2])) return { ok: false, empty: false, error: 'Use pesos and centavos, like 12.50.' };
  const whole = m[1] === '' ? 0 : Number(m[1]);
  const cents = Number((m[2] || '').padEnd(2, '0'));
  const centavos = whole * 100 + cents;
  if (!Number.isSafeInteger(centavos) || centavos > max) return { ok: false, empty: false, error: `That is more than ${maxLabel(max)}. Check the amount.` };
  return { ok: true, centavos };
}

/** @param {number} max */
function maxLabel(max) {
  return `₱${String(Math.floor(max / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

/**
 * Integer centavos → the text a person edits: 1250 → "12.50", 4900 → "49", 5 → "0.05".
 * @param {number | null | undefined} centavos
 */
export function centavosToInput(centavos) {
  if (centavos === null || centavos === undefined || !Number.isFinite(centavos)) return '';
  const c = Math.round(Math.abs(centavos));
  const whole = Math.floor(c / 100);
  const cents = c % 100;
  return (centavos < 0 ? '-' : '') + (cents ? `${whole}.${String(cents).padStart(2, '0')}` : String(whole));
}
